import React, { useState, useEffect } from "react";
import { ModalWrapper } from "./ModalWrapper";
import { POPUP_STYLES } from "../../constants/popupStyles";

interface TwoFactorVerifyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onVerify: (token: string) => void;
    /** Inline error to show under the input (e.g. a rejected code). */
    error?: string;
    /** True while the verify request is in flight. */
    isVerifying?: boolean;
}

export const TwoFactorVerifyModal: React.FC<TwoFactorVerifyModalProps> = ({
    isOpen,
    onClose,
    onVerify,
    error = "",
    isVerifying = false,
}) => {
    const [token, setToken] = useState("");
    // Show the parent's error until the user edits the code again, so a stale
    // "invalid code" message does not linger while they retype.
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        if (error) {
            setShowError(true);
        }
    }, [error]);

    const handleVerify = () => {
        // Keep the code in the field on failure so the user can fix a typo; the
        // parent closes the modal on success. Ignore clicks while verifying.
        if (token.trim() && !isVerifying) {
            onVerify(token);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setToken(e.target.value);
        if (showError) {
            setShowError(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && token.trim() && !isVerifying) {
            handleVerify();
        }
    };

    return (
        <ModalWrapper modalId="TwoFactorVerifyModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Two-Factor Authentication"
        >
            <div>
                <div className="mb-6">
                    <input
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={token}
                        onChange={handleChange}
                        onKeyPress={handleKeyPress}
                        maxLength={6}
                        autoFocus
                        disabled={isVerifying}
                        className={POPUP_STYLES.input}
                    />
                    {showError && error ? (
                        <p className="text-xs text-red-600 mt-2 text-center">
                            {error}
                        </p>
                    ) : (
                        <p className="text-xs text-[#366A75] mt-2 text-center">
                            Enter the 6-digit code from your authenticator app
                        </p>
                    )}
                </div>

                <div className="flex gap-4 justify-center">
                    <button
                        onClick={onClose}
                        disabled={isVerifying}
                        className={POPUP_STYLES.button}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleVerify}
                        disabled={!token.trim() || isVerifying}
                        className={POPUP_STYLES.button}
                    >
                        {isVerifying ? "Verifying..." : "Verify"}
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
